import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Button from 'components/inputs/button';
import NumberInput from 'components/inputs/number';
import Text from 'components/inputs/text';
import {
  ScoreDefinitionAdminType,
  ScoringParameterAdminType
} from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { errorModal, successModal } from 'utils/ui';

const CATEGORY_LABELS: Record<string, string> = {
  comfortPerformance: 'Comfort & Performance',
  installationDifficulty: 'Installation Difficulty',
  noise: 'Noise',
  value: 'Value',
  penalties: 'Penalties',
  bonuses: 'Bonuses',
  total: 'Overall'
};

const ORPHAN_GROUP_LABELS: Record<string, string> = {
  Z: 'Builder Tunables: Zone / Air-Handler Rollups',
  Y: 'Builder Tunables: System Rollups',
  SR: 'Builder Tunables: Scenario Risk Thresholds'
};

const LEVEL_COLORS: Record<string, string> = {
  Subscore: 'bg-purple-100 text-purple-700',
  Component: 'bg-gray-100 text-gray-700',
  Penalty: 'bg-red-100 text-red-700',
  Bonus: 'bg-green-100 text-green-700'
};

const paramKey = (p: { scoreCode: string; paramKey: string }) =>
  `${p.scoreCode}::${p.paramKey}`;

export default function ScoreDefinitionsPage() {
  const dispatch = useAppDispatch();
  const { scoreDefinitions, scoringParameters } = useAppSelector(
    (state) => state.admin
  );
  const [localParameters, setLocalParameters] = useState<
    ScoringParameterAdminType[]
  >([]);
  const [search, setSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    setLocalParameters(scoringParameters);
  }, [scoringParameters]);

  const originalByKey = useMemo(() => {
    const map = new Map<string, ScoringParameterAdminType>();
    for (const p of scoringParameters) {
      map.set(paramKey(p), p);
    }
    return map;
  }, [scoringParameters]);

  const hasChanges = useMemo(() => {
    if (localParameters.length !== scoringParameters.length) return true;
    return localParameters.some((p) => {
      const original = originalByKey.get(paramKey(p));
      return !original || original.paramValue !== p.paramValue;
    });
  }, [localParameters, scoringParameters, originalByKey]);

  const parametersByScoreCode = useMemo(() => {
    const map = new Map<string, ScoringParameterAdminType[]>();
    for (const p of localParameters) {
      if (!map.has(p.scoreCode)) {
        map.set(p.scoreCode, []);
      }
      map.get(p.scoreCode)!.push(p);
    }
    return map;
  }, [localParameters]);

  const matchesSearch = (text: string | null | undefined): boolean => {
    if (!search) return true;
    return (text ?? '').toLowerCase().includes(search.toLowerCase());
  };

  const filteredDefinitions = useMemo(() => {
    if (!search) return scoreDefinitions;
    return scoreDefinitions.filter((d) => {
      if (
        matchesSearch(d.scoreCode) ||
        matchesSearch(d.categoryCode) ||
        matchesSearch(d.scoreName) ||
        matchesSearch(d.notes)
      ) {
        return true;
      }
      const params = parametersByScoreCode.get(d.scoreCode) ?? [];
      return params.some(
        (p) => matchesSearch(p.paramKey) || matchesSearch(p.label)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreDefinitions, search, parametersByScoreCode]);

  const groupedDefinitions = useMemo(() => {
    const groups = new Map<string, ScoreDefinitionAdminType[]>();
    for (const d of filteredDefinitions) {
      if (!groups.has(d.categoryCode)) {
        groups.set(d.categoryCode, []);
      }
      groups.get(d.categoryCode)!.push(d);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredDefinitions]);

  const orphanGroups = useMemo(() => {
    const definedScoreCodes = new Set(scoreDefinitions.map((d) => d.scoreCode));
    const groups = new Map<string, ScoringParameterAdminType[]>();
    const orphans = localParameters.filter(
      (p) => !definedScoreCodes.has(p.scoreCode)
    );
    const matching = search
      ? orphans.filter(
          (p) =>
            matchesSearch(p.paramKey) ||
            matchesSearch(p.label) ||
            matchesSearch(p.scoreCode)
        )
      : orphans;
    for (const p of matching) {
      if (!groups.has(p.scoreCode)) groups.set(p.scoreCode, []);
      groups.get(p.scoreCode)!.push(p);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localParameters, scoreDefinitions, search]);

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const updateValue = (scoreCode: string, pKey: string, paramValue: number) => {
    setLocalParameters((prev) =>
      prev.map((p) =>
        p.scoreCode === scoreCode && p.paramKey === pKey
          ? { ...p, paramValue }
          : p
      )
    );
  };

  const handleSave = async () => {
    try {
      const changed = localParameters.filter((p) => {
        const original = originalByKey.get(paramKey(p));
        return original && original.paramValue !== p.paramValue;
      });
      if (changed.length === 0) {
        successModal('No changes to save.');
        return;
      }
      await axios.patch('admin/scoring-parameters', changed);
      dispatch(fetchDefaultAdminData());
      successModal(
        `Updated ${changed.length} tunable${changed.length !== 1 ? 's' : ''}.`
      );
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    setLocalParameters(scoringParameters);
  };

  const renderTunablesPanel = (params: ScoringParameterAdminType[]) => (
    <div className='mt-2 flex flex-col gap-1 rounded border border-gray-100 bg-gray-50 p-2'>
      <div className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
        Tunables
      </div>
      {params.map((p) => {
        const original = originalByKey.get(paramKey(p));
        const dirty = original && original.paramValue !== p.paramValue;
        return (
          <div key={paramKey(p)} className='flex items-start gap-3 text-sm'>
            <span className='w-72 shrink-0 break-all font-mono text-gray-700'>
              {p.paramKey}
            </span>
            <span
              className={
                p.label
                  ? 'min-w-0 flex-1 break-words text-gray-600'
                  : 'min-w-0 flex-1 italic text-gray-400'
              }
            >
              {p.label ?? '(no label)'}
            </span>
            <div className='w-32 shrink-0'>
              <NumberInput
                value={p.paramValue}
                onChange={(value) =>
                  updateValue(p.scoreCode, p.paramKey, value ?? 0)
                }
                step={0.01}
                size='sm'
              />
            </div>
            <span
              className={
                dirty
                  ? 'w-20 shrink-0 pt-1 text-right text-xs font-semibold uppercase tracking-wide text-amber-600'
                  : 'w-20 shrink-0'
              }
            >
              {dirty ? 'Modified' : ''}
            </span>
          </div>
        );
      })}
    </div>
  );

  const totalDefinitionCount = filteredDefinitions.length;
  const totalOrphanParamCount = orphanGroups.reduce(
    (sum, [, params]) => sum + params.length,
    0
  );

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      {hasChanges && (
        <div className='sticky top-0 z-10 -mx-5 -mt-5 mb-1 border-b border-amber-200 bg-amber-50/95 px-5 py-3 shadow-sm backdrop-blur'>
          <div className='mx-auto flex max-w-[1600px] items-center gap-3'>
            <span className='text-sm font-semibold text-amber-800'>
              You have unsaved tunable changes.
            </span>
            <div className='ml-auto flex gap-2'>
              <Button text='Save' color='green' onClick={handleSave} />
              <Button text='Reset' color='red' onClick={handleReset} />
            </div>
          </div>
        </div>
      )}
      <div className='text-3xl font-bold text-gray-900'>Score Definitions</div>
      <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800'>
        Definitions (scoreCode, level, notes) are code-managed and ship with the
        scoring engine. Tunable values can be edited and saved here.
      </div>
      <div className='flex items-center gap-3'>
        <Button
          text='Save'
          color='green'
          onClick={handleSave}
          disabled={!hasChanges}
        />
        <Button
          text='Reset'
          color='red'
          onClick={handleReset}
          disabled={!hasChanges}
        />
        <div className='ml-auto w-72'>
          <Text
            value={search}
            onChange={setSearch}
            placeholder='Search code, name, category, paramKey, or label...'
            controlled
          />
        </div>
      </div>

      <div className='flex flex-col gap-3'>
        {groupedDefinitions.map(([categoryCode, definitions]) => {
          const collapsed = collapsedGroups.has(categoryCode);
          const Icon = collapsed ? ChevronRightIcon : ChevronDownIcon;
          return (
            <div
              key={categoryCode}
              className='rounded-lg border border-gray-200 bg-white shadow-sm'
            >
              <button
                type='button'
                onClick={() => toggleGroup(categoryCode)}
                className='flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-gray-50'
              >
                <Icon className='h-5 w-5 text-gray-500' />
                <span className='font-semibold text-gray-900'>
                  {CATEGORY_LABELS[categoryCode] ?? categoryCode}
                </span>
                <span className='rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600'>
                  {categoryCode}
                </span>
                <span className='text-sm text-gray-500'>
                  {definitions.length} definition
                  {definitions.length !== 1 ? 's' : ''}
                </span>
              </button>
              {!collapsed && (
                <div className='border-t border-gray-100'>
                  {definitions.map((def) => {
                    const params =
                      parametersByScoreCode.get(def.scoreCode) ?? [];
                    return (
                      <div
                        key={def.scoreCode}
                        className='flex items-start gap-4 border-b border-gray-100 px-4 py-3 last:border-b-0'
                      >
                        <span className='w-24 shrink-0 rounded bg-blue-100 px-2 py-0.5 text-center font-mono text-sm font-semibold text-blue-700'>
                          {def.scoreCode}
                        </span>
                        <div className='min-w-0 flex-1'>
                          <div className='flex flex-wrap items-center gap-2'>
                            <span className='font-semibold text-gray-900'>
                              {def.scoreName}
                            </span>
                            <span
                              className={clsx(
                                'rounded px-2 py-0.5 text-xs font-semibold',
                                LEVEL_COLORS[def.level] ??
                                  'bg-gray-100 text-gray-700'
                              )}
                            >
                              {def.level}
                            </span>
                          </div>
                          {def.notes && (
                            <div className='mt-1 text-sm text-gray-500'>
                              {def.notes}
                            </div>
                          )}
                          {params.length > 0 && renderTunablesPanel(params)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {orphanGroups.length > 0 && (
          <div className='mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Builder Tunables (no top-level score definition)
          </div>
        )}
        {orphanGroups.map(([scoreCode, params]) => {
          const groupKey = `orphan::${scoreCode}`;
          const collapsed = collapsedGroups.has(groupKey);
          const Icon = collapsed ? ChevronRightIcon : ChevronDownIcon;
          return (
            <div
              key={groupKey}
              className='rounded-lg border border-gray-200 bg-white shadow-sm'
            >
              <button
                type='button'
                onClick={() => toggleGroup(groupKey)}
                className='flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-gray-50'
              >
                <Icon className='h-5 w-5 text-gray-500' />
                <span className='font-semibold text-gray-900'>
                  {ORPHAN_GROUP_LABELS[scoreCode] ?? `Tunables: ${scoreCode}`}
                </span>
                <span className='rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600'>
                  {scoreCode}
                </span>
                <span className='text-sm text-gray-500'>
                  {params.length} tunable
                  {params.length !== 1 ? 's' : ''}
                </span>
              </button>
              {!collapsed && (
                <div className='border-t border-gray-100 px-4 py-3'>
                  <div className='mb-2 text-sm text-gray-500'>
                    These tunables are consumed by the input-builder pipeline
                    and have no top-level score definition.
                  </div>
                  {renderTunablesPanel(params)}
                </div>
              )}
            </div>
          );
        })}

        {groupedDefinitions.length === 0 && orphanGroups.length === 0 && (
          <div className='py-8 text-center text-gray-500'>
            No definitions or tunables match your search.
          </div>
        )}
      </div>
      <div className='text-sm text-gray-400'>
        {totalDefinitionCount} definition
        {totalDefinitionCount !== 1 ? 's' : ''} across{' '}
        {groupedDefinitions.length} categor
        {groupedDefinitions.length !== 1 ? 'ies' : 'y'}
        {orphanGroups.length > 0 && (
          <>
            {' '}
            + {totalOrphanParamCount} builder tunable
            {totalOrphanParamCount !== 1 ? 's' : ''} across{' '}
            {orphanGroups.length} namespace
            {orphanGroups.length !== 1 ? 's' : ''}
          </>
        )}
      </div>
    </div>
  );
}
